import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, User, Mail, Phone, Lock, CheckCircle, AlertCircle } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useTranslation } from "react-i18next";

const Auth = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupEmail, setSignupEmailState] = useState("");
  const [signupUserId, setSignupUserId] = useState("");
  const [signupUserName, setSignupUserName] = useState("");
  const { toast } = useToast();
  const navigate = useNavigate();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const [signupName, setSignupName] = useState("");
  const [signupEmailInput, setSignupEmail] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirmPassword, setSignupConfirmPassword] = useState("");

  const [resendingEmail, setResendingEmail] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  const checkEmailVerified = async () => {
    if (!signupUserId) return false;
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("email_verified")
        .eq("id", signupUserId)
        .single();
      if (error) return false;
      return data?.email_verified === true;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    if (signupSuccess && signupUserId) {
      pollingRef.current = setInterval(async () => {
        const verified = await checkEmailVerified();
        if (verified) {
          if (pollingRef.current) clearInterval(pollingRef.current);
          toast({ title: "E-mail confirmado!", description: "Sua conta foi ativada. Redirecionando..." });
          setTimeout(() => {
            setSignupSuccess(false);
            navigate("/auth");
          }, 2000);
        }
      }, 5000);
      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
      };
    }
  }, [signupSuccess, signupUserId, navigate, toast]);

  const checkIsAdmin = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .single();
    return !!data;
  };

  useEffect(() => {


    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const { data: profile } = await supabase.from("profiles").select("email_verified").eq("id", session.user.id).single();
        if (profile?.email_verified) {
          const isAdmin = await checkIsAdmin(session.user.id);
          navigate(isAdmin ? "/admin" : "/agendamento-seu-ze");
        }
      }
      setCheckingSession(false);
    });

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const { data: profile } = await supabase.from("profiles").select("email_verified").eq("id", session.user.id).single();
        if (profile?.email_verified) {
          const isAdmin = await checkIsAdmin(session.user.id);
          navigate(isAdmin ? "/admin" : "/agendamento-seu-ze");
        }
      }
      setCheckingSession(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email: loginEmail, password: loginPassword });

      if (error) throw error;
      if (data.user) {
        const { data: profile } = await supabase.from("profiles").select("email_verified").eq("id", data.user.id).single();
        if (!profile?.email_verified) {
          await supabase.auth.signOut();
          setSignupSuccess(true);
          setSignupEmailState(loginEmail);
          setSignupUserId(data.user.id);
          toast({ title: "E-mail não confirmado", description: "Por favor, confirme seu e-mail antes de acessar.", variant: "destructive" });
          return;
        }
        const isAdmin = await checkIsAdmin(data.user.id);
        toast({ title: t('auth.loginSuccess'), description: t('auth.loginSuccessDesc') });
        navigate(isAdmin ? "/admin" : "/agendamento-seu-ze");
      }
    } catch (error: any) {
      let message = error.message;
      if (error.message === "Invalid login credentials") message = t('auth.invalidCredentials');
      toast({ title: t('auth.loginError'), description: message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, { redirectTo: `${window.location.origin}/reset-password` });
      if (error) throw error;
      toast({ title: t('auth.resetEmailSent'), description: t('auth.resetEmailSentDesc') });
      setShowForgotPassword(false);
    } catch (error: any) {
      toast({ title: t('auth.error'), description: error.message, variant: "destructive" });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signupPassword !== signupConfirmPassword) {
      toast({ title: t('auth.error'), description: t('auth.passwordMismatch'), variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: signupEmailInput,
        password: signupPassword,
        options: { data: { full_name: signupName, phone: signupPhone } }
      });
      if (error) throw error;
      if (data.user) {
        setSignupSuccess(true);
        setSignupEmailState(signupEmailInput);
        setSignupUserId(data.user.id);
      }
    } catch (error: any) {
      toast({ title: t('auth.signupError'), description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (signupSuccess) {
    return (
      <Layout>
        <div className="min-h-screen flex items-start justify-center pt-[25px] px-4">
          <Card className="w-full max-w-md text-center p-6">
            <Mail className="mx-auto mb-4 w-12 h-12 text-primary" />
            <CardTitle className="mb-2">Confirme seu E-mail</CardTitle>
            <p className="mb-4">{signupEmail}</p>
            <Button variant="outline" onClick={() => setSignupSuccess(false)}>Voltar para Login</Button>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO title={t('auth.seoTitle')} description={t('auth.seoDescription')} />
      <div className="min-h-screen flex items-start justify-center bg-gradient-to-b from-background to-muted/30 pt-[25px] pb-20 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-display text-primary">{t('auth.title')}</CardTitle>
            <CardDescription>{t('auth.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">{t('auth.login')}</TabsTrigger>
                <TabsTrigger value="signup">{t('auth.signup')}</TabsTrigger>
              </TabsList>
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      E-mail ou Usuário
                    </Label>
                    <Input
                      id="login-email"
                      type="text"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="seu@email.com ou Usuário"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Senha</Label>
                    <Input id="login-password" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : t('auth.login')}
                  </Button>
                </form>
              </TabsContent>
              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-4">
                   <div className="space-y-2">
                    <Label htmlFor="signup-name">Nome Completo</Label>
                    <Input id="signup-name" value={signupName} onChange={(e) => setSignupName(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">E-mail</Label>
                    <Input id="signup-email" type="email" value={signupEmailInput} onChange={(e) => setSignupEmail(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Senha</Label>
                    <Input id="signup-password" type="password" value={signupPassword} onChange={(e) => setSignupPassword(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm">Confirmar Senha</Label>
                    <Input id="signup-confirm" type="password" value={signupConfirmPassword} onChange={(e) => setSignupConfirmPassword(e.target.value)} required />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>Cadastrar</Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Auth;
